/* eslint-disable camelcase */
/* eslint-disable no-unused-expressions */
import React, { useEffect, useRef, useState, useContext } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, GridOptions } from 'ag-grid-community';
import { PNCounterIcon } from '@mummssoftware/global-context/PNCounter';
import { VFGlobalContext } from '@mummssoftware/global-context/VFGlobalContext';
import { ENTITY_PATIENT_CARE_PLAN_FREQUENCY } from '@mummssoftware/global-context/PNCounter/constants';
import { NotAssigned } from '@mummssoftware/utils/frequencies/frequency-constants';
import { CPFContext } from '../../containers/Context/CPFContext';
import {
  SET_EDITING_ROLEGROUP,
  SET_API_ERROR,
} from '../../containers/Context/constants';
import { getRoleGroupMaster } from '../agGridCallBacks';
import EditEffectiveCell from '../cells/EditEffectiveCell';
import EditFrequencyCell from '../cells/EditFrequencyCell';
import EditWorkerCell from '../cells/EditWorkerCell';
import EditRowCell from '../cells/EditRowCell';
import { OptionsCell } from '../cells/OptionsCell';
import { EditDaysOfWeekCell } from '../cells/EditDaysOfWeekCell';
import { PrnCell } from '../cells/PrnCell';
import { PrnDetailCell } from '../cells/PrnDetailCell';
import {
  CareProvider,
  SpecificDays,
  Effective,
  Frequency,
  PlusPRN,
  RoleGroupString,
} from '../../utils/constant';
import defaults from '../../translations/en.json';
import {
  getWidgetEditMode,
  canStartInlineEdit,
  shouldShowPencilColumn,
  getInlineEditDenyToastKey,
  CpfEditColumn,
} from '../../editPolicy';

import 'ag-grid-community/dist/styles/ag-grid.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';

// TODO build a Visit Interface with proper key when we know exactly when we need
interface CarePlanFrequencyTableProps {
  smallWidget?: boolean;
  viewModePrint?: boolean;
  handleSubmit?: (arg0: any, editWorker: any) => void;
  rgHash: mumms.HashedRoleGroup;
  readOnly?: boolean;
}
let gridApi: GridApi | null;

/**
 * @component
 * Styled Care Plan Frequency Table. Pass plans and functions callbacks
 */
// TODO: TRANSLATIONS
export const CarePlanFrequencyTable = ({
  smallWidget,
  viewModePrint,
  handleSubmit,
  rgHash,
  readOnly,
}: CarePlanFrequencyTableProps) => {
  const gridApiRef = useRef(gridApi);
  // ag-grid stores cellRendererFramework references at init, so closures over
  // `rgHash` become stale after order save. A ref lets even stale closures read
  // the current hash at render time without needing ag-grid to re-initialise columns.
  const rgHashRef = useRef<mumms.HashedRoleGroup>(rgHash);
  // ag-grid keeps the initial onCellClicked closure — read current edit lock via ref.
  const editingRoleGroupIdRef = useRef<string | null>(null);
  const {
    dispatch,
    agRowData,
    isPRNOrderAgency,
    isCPFAgency,
    editingRoleGroupId,
  } = useContext(CPFContext);
  const { VFordersMode } = useContext(VFGlobalContext);
  const [showGridDropDown] = useState<boolean>(false);

  // Keep rgHashRef current for cell renderers / detail rows. Do NOT force
  // refreshCells/redrawRows here — AgGridReact already applies rowData={agRowData}.
  // Profiling (save flicker): that force redraw ran on every SET_AG_ROWS and
  // stacked with React updates into multiple AgGridReact paints.
  useEffect(() => {
    rgHashRef.current = rgHash;
  }, [rgHash]);
  const showGridDropDownRef = useRef(showGridDropDown);
  useEffect(() => {
    showGridDropDownRef.current = showGridDropDown;
  }, [showGridDropDown]);
  useEffect(() => {
    editingRoleGroupIdRef.current =
      editingRoleGroupId == null ? null : String(editingRoleGroupId);
  }, [editingRoleGroupId]);

  // Stale onCellClicked closure — keep mode inputs current via refs.
  const isPRNOrderAgencyRef = useRef(isPRNOrderAgency);
  const isCPFAgencyRef = useRef(isCPFAgency);
  const VFordersModeRef = useRef(VFordersMode);
  useEffect(() => {
    isPRNOrderAgencyRef.current = isPRNOrderAgency;
    isCPFAgencyRef.current = isCPFAgency;
    VFordersModeRef.current = VFordersMode;
  }, [isPRNOrderAgency, isCPFAgency, VFordersMode]);

  const {
    props: { grid: gridTheme },
  } = useTheme() as any;

  const isRowMasterBySize = (data: mumms.RoleGroup) =>
    viewModePrint ? false : getRoleGroupMaster(data, rgHashRef.current);

  // cell renderers below for table cells.
  const OptionsRenderer = (props: any) => (
    <OptionsCell
      data={props.data}
      node={props.node}
      gridOptions={gridOptions}
      handleSubmit={handleSubmit}
      rgHash={rgHashRef.current}
      style={{ height: 100 }}
    />
  );

  const noteCountCellRenderer = (props: any) => {
    const { id, roleGroupId, name, patientNumber } = props.data;
    if (typeof id === 'string' && !viewModePrint) {
      if (id.startsWith('pending-')) return null;
      const carePlan = rgHashRef.current[roleGroupId]?.frequencies?.[id];
      return (
        <PNCounterIcon
          patientNumber={patientNumber}
          entityKey={`${patientNumber}-${name}`}
          entityType={ENTITY_PATIENT_CARE_PLAN_FREQUENCY}
          entityName={`${carePlan?.discipline || NotAssigned}`}
        />
      );
    }
    return null;
  };

  const DaysOfWeekRenderer = (props: any) => (
    <EditDaysOfWeekCell
      data={props.data}
      rgHash={rgHashRef.current}
      node={props.node}
    />
  );

  const WorkerRenderer = (props: any) => (
    <EditWorkerCell
      data={props.data}
      node={props.node}
      gridApiRef={gridApiRef}
      rgHash={rgHashRef.current}
      style={{ height: 100 }}
    />
  );

  const FrequencyRenderer = (props: any) => (
    <EditFrequencyCell
      data={props.data}
      node={props.node}
      api={props.api}
      rgHash={rgHashRef.current}
      style={{ height: 100 }}
    />
  );

  const EffectiveRenderer = (props: any) => (
    <EditEffectiveCell
      data={props.data}
      node={props.node}
      rgHash={rgHashRef.current}
      style={{ height: 100 }}
    />
  );

  const PrnRenderer = (props: any) => (
    <PrnCell
      data={props.data}
      node={props.node}
      rgHash={rgHashRef.current}
      style={{ height: 100 }}
      viewModePrint={viewModePrint}
    />
  );

  const PrnDetailRenderer = (props: any) => (
    <PrnDetailCell
      data={props.data}
      node={props.node}
      rgHash={rgHashRef.current}
      style={{ height: 100 }}
      viewModePrint={viewModePrint}
    />
  );

  const EditRowRenderer = (props: any) => (
    <EditRowCell
      data={props.data}
      node={props.node}
      gridOptions={gridOptions}
      rgHash={rgHashRef.current}
      style={{ height: 100 }}
      viewModePrint={viewModePrint}
    />
  );

  // returns data for detail (history) rows.
  // Pending order rows use synthetic ids (pending-*) and have no history.
  const getBaseDetailRow = (params: any) => {
    const { id, roleGroupId, isOrderPending } = params.data || {};
    if (
      typeof id === 'number' ||
      isOrderPending ||
      (typeof id === 'string' && id.startsWith('pending-'))
    ) {
      params.successCallback([]);
      return;
    }
    const plan = rgHashRef.current[roleGroupId?.toString()]?.frequencies?.[id];
    params.successCallback(plan?.nonCurrentRows || []);
  };

  // click handler for editing rows, will only trigger on double click of a cell in the
  // frequency, effective, or DOW column.
  const editClick = (gridEvent: any) => {
    if (VFordersModeRef.current) {
      return;
    }
    const { event, data, colDef } = gridEvent;
    const { headerName } = colDef;
    const { id, roleGroupId } = data;
    const roleGroup = rgHashRef.current[roleGroupId.toString()];
    const carePlan: mumms.ProcessedCarePlan =
      roleGroup?.frequencies?.[id] || roleGroup?.frequencies?.dummy;

    const mode = getWidgetEditMode({
      isCPFAgency: !!isCPFAgencyRef.current,
      isPRNOrderAgency: !!isPRNOrderAgencyRef.current,
      VFordersMode: !!VFordersModeRef.current,
    });

    const result = canStartInlineEdit({
      mode,
      column: headerName as CpfEditColumn,
      carePlan,
      assignable: roleGroup?.assignable,
      isDoubleClick: event?.detail === 2,
      smallWidget,
      viewModePrint,
      editingRoleGroupId: editingRoleGroupIdRef.current,
      roleGroupId,
    });

    if (!result.ok) {
      // Double-click that did not start edit — tell the user why (silent for
      // single-click / order mode via getInlineEditDenyToastKey).
      const toastKey = getInlineEditDenyToastKey(result.reason);
      if (toastKey) {
        const text = defaults[toastKey as keyof typeof defaults];
        if (text) {
          dispatch({
            type: SET_API_ERROR,
            payload: {
              text,
              severity: 'info',
            },
          });
        }
      }
      return;
    }

    const alreadyEditing =
      editingRoleGroupIdRef.current != null &&
      editingRoleGroupIdRef.current === String(roleGroupId);

    dispatch({
      type: SET_EDITING_ROLEGROUP,
      payload: data.roleGroupId,
    });
    editingRoleGroupIdRef.current = String(data.roleGroupId);
    // First lock only — same-row re-click (e.g. open Specific Days) must not
    // redrawRows or Frequency remounts and re-seeds (Way 2 flicker/revert).
    if (!alreadyEditing) {
      setTimeout(() => {
        gridOptions.api?.redrawRows();
      }, 200);
    }
  };

  const widgetMode = getWidgetEditMode({
    isCPFAgency: !!isCPFAgency,
    isPRNOrderAgency: !!isPRNOrderAgency,
    VFordersMode,
  });
  const editColum = shouldShowPencilColumn(widgetMode)
    ? {
        headerName: '',
        field: 'editRow',
        suppressMenu: true,
        suppressSizeToFit: true,
        resizable: false,
        width: 65,
        cellRendererFramework: EditRowRenderer,
      }
    : {
        headerName: '',
        field: 'options',
        cellRenderer: 'optionMenuButton',
        cellRendererParams: {
          showOptions: showGridDropDownRef.current,
        },
        suppressMenu: true,
        suppressSizeToFit: true,
        resizable: false,
        width: 75,
      };
  const readOnlyColumns = [
    {
      headerName: '',
      field: 'role_group_name',
      cellRenderer: 'noteCountCellRenderer',
      cellClass: 'no-border no-pad',
      width: 33,
      suppressMenu: true,
    },
    {
      headerName: RoleGroupString,
      field: 'name',
      suppressMenu: true,
      resizable: false,
      maxWidth: 130,
      cellRenderer: 'agGroupCellRenderer',
    },
    {
      headerName: Frequency,
      field: 'frequency',
      suppressMenu: true,
      suppressSizeToFit: true,
      resizable: false,
      width: smallWidget ? 75 : 130,
      cellRendererFramework: FrequencyRenderer,
    },
    {
      headerName: PlusPRN,
      field: 'prn',
      suppressMenu: true,
      suppressSizeToFit: true,
      resizable: false,
      width: 65,
      cellRendererFramework: PrnRenderer,
    },
    {
      headerName: CareProvider,
      field: 'worker',
      suppressMenu: true,
      resizable: false,
      width: 160,
      cellRendererFramework: WorkerRenderer,
    },

    {
      headerName: SpecificDays,
      suppressMenu: true,
      suppressSizeToFit: true,
      resizable: false,
      // eslint-disable-next-line no-nested-ternary
      width: smallWidget ? 75 : viewModePrint ? 150 : 175,
      cellRendererFramework: DaysOfWeekRenderer,
    },
    {
      headerName: Effective,
      field: 'effective',
      suppressMenu: true,
      suppressSizeToFit: true,
      resizable: false,
      width: 85,
      cellRendererFramework: EffectiveRenderer,
    },
  ];

  // test options below are commented out in case of future UI funkyness
  const gridOptions: GridOptions = {
    // embedFullWidthRows: true,
    masterDetail: !smallWidget,
    isRowMaster: isRowMasterBySize,
    embedFullWidthRows: true,
    // domLayout: 'autoHeight',
    getRowHeight: (params: any) => (smallWidget ? 26 : params.data.rowHeight),
    onCellClicked: (e) => editClick(e),
    // Prevents the scroll bar from overlapping grid content.
    suppressHorizontalScroll: true,
    columnDefs: readOnly ? readOnlyColumns : [...readOnlyColumns, editColum],
    detailCellRendererParams: {
      detailGridOptions: {
        columnDefs: [
          {
            field: 'space', // note count
            suppressSizeToFit: true,
            resizable: false,
            width: 33,
          },
          {
            field: '', // role group
            suppressSizeToFit: true,
            resizable: false,
            width: 130,
          },
          {
            field: 'frequencyText',
            suppressSizeToFit: true,
            resizable: false,
            width: 130,
          },
          {
            field: 'prn',
            suppressSizeToFit: true,
            resizable: false,
            width: 65,
            cellRendererFramework: PrnDetailRenderer,
          },
          {
            field: '', // care provider
            suppressSizeToFit: true,
            resizable: false,
            width: 160,
          },
          {
            field: 'daysOfWeek',
            suppressSizeToFit: true,
            resizable: false,
            width: 175,
          },
          {
            field: 'effectiveDate',
            suppressSizeToFit: true,
            resizable: false,
            width: 85,
          },
        ],
        rowHeight: 25,

        // prevent ag grid scrollbar overlapping last list item in detail grid
        suppressHorizontalScroll: true,
      },
      getDetailRowData: getBaseDetailRow,
    },
    groupHeaderHeight: 20,
    detailRowAutoHeight: true,
    frameworkComponents: {
      optionMenuButton: OptionsRenderer,
      noteCountCellRenderer,
    },
    onGridReady: (params: any) => {
      gridApi = params.api;
      gridApiRef.current = gridApi || params.api;
      const gridColumnApi = params.columnApi;

      // hide the worker and options column if the widget is compressed.
      gridColumnApi.setColumnsVisible(['worker'], !smallWidget);
      // also hide the options column in print view
      gridColumnApi.setColumnsVisible(
        ['options'],
        !smallWidget && !viewModePrint,
      );
      if (gridApi) {
        if (viewModePrint) {
          gridApi.setDomLayout('print');
        }
      }
    },
  };

  return (
    <AgGridReact
      gridOptions={gridOptions}
      // rowData={agRowData.length ? [agRowData[3]] : agRowData}
      rowData={agRowData}
      animateRows
      rowSelection="multiple"
      suppressRowClickSelection
      /** https://www.ag-grid.com/javascript-grid/grid-callbacks/#reference-callbacks */
      rowHeight={gridTheme.rowHeight}
      headerHeight={gridTheme.headerHeight}
    />
  );
};

export default CarePlanFrequencyTable;
